/*
 * ## ⭐⭐ 「정본 그대로 이관」에서 ★ 이 파일은 ⇒ ⭐ «뺀다» (2026-10-10 · 관제 결정)
 *
 * ⛔ 덮으면 ★ 우리가 ⭐ 합친 «셈 한 벌»이 ⇒ ★ 다시 ⭐ 둘로 갈린다:
 *
 *   정본 ⇒ Math.round((Date.parse(p.dueDate) - Date.parse(today)) / 86400_000)   ⇐ ★ 인라인
 *   우리 ⇒ dueDayDiff(p.dueDate)                                                ⇐ ⭐ SSOT
 *
 * ⇒ ⭐ 화면 결과는 ★ «같다» — ⇒ 그래서 ⛔ 덮을 ★ 이득이 ⭐ 0 이고 ⇒ 잃는 것만 있다.
 * ⇒ ★ 그 「한 벌」을 ⭐ project-dday-ssot.guard.test.ts 가 ★ 잠근다
 *   ⇒ ⇒ ★ 그 가드의 까닭 ⇒ ⭐ 「사본이 있는데 ★ 아무도 맞대지 않았다」(2026-10-09).
 *
 * ⏳ 만료 조건 — ★ «내놓는 이름»으로
 *   ⇒ ⭐ `./my-task-due` 가 ★ `dueDayDiff` «라는 이름»을 ⇒ ⛔ 더는 안 내놓는 날
 *      ⇒ ⇒ ★ 그때는 ⭐ 인라인이 «유일한 셈»이 되므로 ⇒ 정본 글자로 덮어도 ★ 잃는 것이 없다.
 *   ⇒ ⛔ 「그 파일이 있다」로 재지 ★ 마라 — ⇒ ⭐ 지금도 있다. 가르는 것은 ★ «그 export»다.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { Briefcase, Plus, ArrowRight, Trash2 } from '../../icons';
import { listTasks, type TaskProjectDto } from '../../api/tasks';
import { stripColor } from '../TaskPage/ProjectTopBar';
import { Skeleton } from '../../components/shared';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import { dueDayDiff } from './my-task-due';
import { isProjectCreator } from './confirmDeleteProject';

interface TeamProjectListProps {
  projects: TaskProjectDto[];
  loading: boolean;
  onOpen: (projectId: string) => void;
  onAddProject: () => void;
  /** v2.3 (2026-06-11) — 전달 시 행에 삭제 버튼 노출 (미전달 = 기존 동작 무변경). */
  onDelete?: (project: TaskProjectDto) => void;
}

/**
 * TeamProjectList (project-menu-detailing) — 선택 팀(또는 개인)의 프로젝트 목록 + 진행률 바.
 *
 * 진행률: 서버 집계(doneTaskCount/taskCount) 우선 — review loop1 보류분 해소.
 * 서버 필드 부재(구버전 응답·단건 유래 DTO) 시에만 기존 listTasks fan-out fallback (상위 12개 한정).
 */
function serverPct(p: TaskProjectDto): number | undefined {
  if (p.doneTaskCount === undefined) return undefined;
  return p.taskCount > 0 ? Math.round((p.doneTaskCount / p.taskCount) * 100) : 0;
}

export function TeamProjectList({ projects, loading, onOpen, onAddProject, onDelete }: TeamProjectListProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const [progress, setProgress] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // 서버 집계 필드가 없는 프로젝트만 fallback fan-out (전부 있으면 0 콜).
      const targets = projects.filter((p) => serverPct(p) === undefined).slice(0, 12);
      if (targets.length === 0) return;
      const results = await Promise.allSettled(
        targets.map(async (p) => {
          const tasks = await listTasks(p.id);
          const total = tasks.length;
          const done = tasks.filter((t) => t.status === 'DONE').length;
          return { id: p.id, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
        }),
      );
      if (cancelled) return;
      const next: Record<string, number> = {};
      for (const r of results) {
        if (r.status === 'fulfilled') next[r.value.id] = r.value.pct;
      }
      setProgress(next);
    })();
    return () => { cancelled = true; };
  }, [projects]);


  return (
    <section className="pj-project-list" aria-label={tProject('project.list.heading')}>
      <div className="pj-project-list-header">
        <h2 className="pj-section-heading">
          <Briefcase size={16} />
          {tProject('project.list.heading')}
          {!loading && <span className="pj-project-count">{projects.length}</span>}
        </h2>
        <button type="button" className="pj-add-project" onClick={onAddProject}>
          <Plus size={13} />
          {tProject('project.list.add')}
        </button>
      </div>

      {loading && (
        <div className="pj-mytasks-skeleton" role="status">
          {[0, 1, 2].map((i) => (
            <div key={i} className="pj-mytasks-skeleton-row" style={{ animationDelay: `${i * 80}ms` }}>
              <Skeleton width="36%" height={13} />
              <Skeleton width="18%" height={11} />
            </div>
          ))}
        </div>
      )}
      {!loading && projects.length === 0 && (
        <div className="pj-empty pj-empty--info" role="status">
          <Briefcase size={14} aria-hidden="true" /> {tProject('project.list.empty')}
        </div>
      )}

      <ul className="pj-project-items pj-project-items--anim">
        {projects.map((p, i) => {
          const pct = serverPct(p) ?? progress[p.id];
          // v2.9.8 — 생성자 본인만 휴지통 노출 (서버 FORBIDDEN_NOT_CREATOR 의 UI 미러)
          const deletable = onDelete !== undefined && isProjectCreator(p);
          // v2.10.2 — 마감 D-day (완료 100% 제외)
          const days = p.dueDate && pct !== 100
            ? dueDayDiff(p.dueDate)
            : null;
          const accent = stripColor(p.id);
          return (
            <li
              key={p.id}
              className={deletable ? 'pj-row-li--actions' : undefined}
              style={{ animationDelay: `${Math.min(i, 10) * 55}ms` }}
            >
              <button
                type="button"
                className="pj-project-row pj-project-row--rich"
                style={{ '--row-accent': accent } as CSSProperties}
                onClick={() => onOpen(p.id)}
                aria-label={tProject('project.list.open_aria', { name: p.name })}
              >
                <span className="pj-project-row-main">
                  <span className="pj-project-name-line">
                    <span className="pj-project-name">{p.name}</span>
                    <span className={`pj-team-chip${p.teamId ? '' : ' pj-team-chip--personal'}`}>
                      {p.teamName ?? tProject('project.list.personal_chip')}
                    </span>
                  </span>
                  <span className="pj-project-meta">
                    {tProject('project.list.task_count', { n: p.taskCount })}
                    {p.doneTaskCount !== undefined ? ` · ${tProject('project.list.done_count', { n: p.doneTaskCount })}` : ''}
                    {p.dueDate ? ` · ~${p.dueDate}` : ''}
                  </span>
                </span>
                <span className="pj-project-row-side">
                  {days !== null && (
                    <span className={`pj-dday pj-dday--${days < 0 ? 'over' : days === 0 ? 'today' : days <= 7 ? 'soon' : 'far'}`}>
                      {days < 0
                        ? tProject('project.detail.stats.dday_over', { n: -days })
                        : days === 0
                          ? tProject('project.detail.stats.dday_today')
                          : tProject('project.detail.stats.dday_left', { n: days })}
                    </span>
                  )}
                  <span
                    className="pj-progress"
                    role="progressbar"
                    aria-valuenow={pct ?? 0}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={tProject('project.list.progress_aria', { percent: pct ?? 0 })}
                  >
                    <span
                      className="pj-progress-fill pj-progress-fill--grow"
                      style={{ width: `${pct ?? 0}%`, animationDelay: `${150 + Math.min(i, 10) * 70}ms` }}
                    />
                  </span>
                  <span className="pj-progress-num">{pct === undefined ? '—' : `${pct}%`}</span>
                  <ArrowRight size={14} className="pj-project-row-arrow" />
                </span>
              </button>
              {deletable && (
                <button
                  type="button"
                  className="pj-row-del"
                  aria-label={tProject('project.list.delete_aria', { name: p.name })}
                  onClick={() => onDelete!(p)}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
